import React, { useEffect, useState } from "react";
import { Container, Row, Col, Form, Button } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import { Mail, Lock, ArrowRight, ShieldCheck, Truck, RotateCcw } from "lucide-react";
import userAPI from "../../api/UserApi";
import { showError, showSuccess } from "../../utils/toastUtils";
import { setAccessToken } from "../../api/axiosInstance";
import useAlphabeticInput from "../../hooks/useAlphabeticInput";
import { handleApiValidationError } from "../../utils/apiErrorHandler";

function LoginCanvas() {
  useAlphabeticInput();
  const navigate = useNavigate();

  const INITIAL_FORM_DATA = {
    email: "",
    password: "",
  };

  const [formData, setFormData] = useState(INITIAL_FORM_DATA);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false); // 🌟 Added loading state to secure multiple hits

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const isValidEmail = emailRegex.test(formData.email);
  const isValidPassword = formData.password.length >= 6;

  const validate = () => {
    const newErrors = {};
    if (!formData.email) {
      newErrors.email = "Please Enter Email Address";
    } else if (!isValidEmail) {
      newErrors.email = "Please enter a valid email";
    }

    if (!formData.password) {
      newErrors.password = "Enter Password";
    } else if (!isValidPassword) {
      newErrors.password = "Password must be at least 6 characters";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) return;

    try {
      setLoading(true);
      const res = await userAPI.login(formData);

      if (res.code === 200) {
        localStorage.setItem(
          "token",
          JSON.stringify({
            email: res.data.email,
            username: res.data.username,
          }),
        );

        localStorage.setItem("accessToken", res.data.accessToken);
        showSuccess("Welcome back to Orangeapes");
        navigate("/users/dashboard");
      }
    } catch (err) {
      handleApiValidationError(err, setErrors);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="orangeapes-login-page-container">
        <Container fluid className="h-100 p-0">
          <Row className="h-100 g-0">
            
            {/* LEFT SIDE: TEXT-BASED EDITORIAL CODES PANEL */}
            <Col lg={6} className="d-none d-lg-block orangeapes-login-text-manifesto-panel">
              <div className="manifesto-content-box">
                <div className="manifesto-top-branding">
                  <span>EST. 2025</span>
                  <h2>ORANGEAPES</h2>
                </div>

                <div className="manifesto-middle-perks-grid">
                  <div className="perk-text-row d-flex gap-3 align-items-start mb-4">
                    <ShieldCheck size={20} className="perk-icon-accent mt-1" />
                    <div>
                      <h6>Secure Boutique Vault</h6>
                      <p>Your orders, premium wishlist matrices, and shipping specifications are guarded with high-grade cryptographic security tokens.</p>
                    </div>
                  </div>

                  <div className="perk-text-row d-flex gap-3 align-items-start mb-4">
                    <Truck size={20} className="perk-icon-accent mt-1" />
                    <div>
                      <h6>Priority Dispatch Channel</h6>
                      <p>Verified profile users gain instant access to faster order tracking processing and local express delivery channels.</p>
                    </div>
                  </div>

                  <div className="perk-text-row d-flex gap-3 align-items-start">
                    <RotateCcw size={20} className="perk-icon-accent mt-1" />
                    <div>
                      <h6>Seamless Returns Blueprint</h6>
                      <p>Initiate dynamic order cancellations, exchange claims, or custom store credits directly from your dashboard grid portal.</p>
                    </div>
                  </div>
                </div>

                <div className="manifesto-footer-copy">
                  <p>&copy; 2026 Orangeapes Studio. Engineered with precise structural minimalism.</p>
                </div>
              </div>
            </Col>

            {/* RIGHT SIDE: MINIMALIST PREMIUM TYPOGRAPHY FORM BOX */}
            <Col
              lg={6}
              className="d-flex justify-content-center align-items-center p-4 p-md-5 bg-white"
            >
              <div className="orangeapes-login-form-card">
                
                <div className="form-header-typography mb-5">
                  <h1 className="orangeapes-login-main-title mb-2">Welcome Back</h1>
                  <p className="orangeapes-login-subtitle">
                    Enter your credentials to access your personal boutique vault dashboard.
                  </p>
                </div>

                <Form method="post" onSubmit={handleSubmit} className="orangeapes-form-element">
                  
                  {/* EMAIL INPUT FIELD */}
                  <Form.Group className="orangeapes-input-group-row mb-4" controlId="email">
                    <Form.Label className="orangeapes-form-custom-label">
                      Email Address <span className="text-orange-token">*</span>
                    </Form.Label>
                    <div className="orangeapes-field-input-wrapper">
                      <Mail size={16} className="orangeapes-field-icon" />
                      <Form.Control
                        type="email"
                        name="email"
                        placeholder="name@example.com"
                        value={formData.email}
                        onChange={handleChange}
                        className="orangeapes-blank-input"
                      />
                    </div>
                    {formData.email && !isValidEmail && (
                      <span className="orangeapes-error-validation-msg">Please enter a valid email</span>
                    )}
                    {errors && errors.email && (
                      <span className="orangeapes-error-validation-msg">{errors.email}</span>
                    )}
                  </Form.Group>

                  {/* PASSWORD INPUT FIELD */}
                  <Form.Group className="orangeapes-input-group-row mb-3" controlId="password">
                    <Form.Label className="orangeapes-form-custom-label">
                      Password <span className="text-orange-token">*</span>
                    </Form.Label>
                    <div className="orangeapes-field-input-wrapper">
                      <Lock size={16} className="orangeapes-field-icon" />
                      <Form.Control
                        type="password"
                        name="password"
                        placeholder="••••••••"
                        value={formData.password}
                        onChange={handleChange}
                        className="orangeapes-blank-input"
                      />
                    </div>
                    {errors && errors.password && (
                      <span className="orangeapes-error-validation-msg">{errors.password}</span>
                    )}
                  </Form.Group>

                  {/* FORGOT LINK ROW */}
                  <div className="d-flex justify-content-end mb-4 pb-2">
                    {/* 🌟 STRUCTURAL VERIFICATION CHECKPOINT: Ensure this matches the exact route parameter in App.jsx */}
                    <Link to="/users/forgot-password" className="orangeapes-forgot-link-text">
                      Forgot Password?
                    </Link>
                  </div>

                  {/* PREMIUM BUTTON SUBMIT */}
                  <Button
                    className="orangeapes-login-submit-btn-solid w-100 py-3 d-flex align-items-center justify-content-center gap-2"
                    type="submit"
                    disabled={loading}
                  >
                    <span>{loading ? "AUTHENTICATING..." : "SIGN IN TO ACCOUNT"}</span>
                    {!loading && <ArrowRight size={14} className="submit-btn-arrow" />}
                  </Button>

                  {/* REDIRECT ANCHOR FOOTER */}
                  <div className="text-center mt-5 pt-3 orangeapes-form-footer-border">
                    <p className="orangeapes-register-redirect-txt">
                      Don't have an account yet?
                      <Link
                        to="/users/register"
                        className="orangeapes-bold-redirect-anchor ms-2"
                      >
                        Register Now
                      </Link>
                    </p>
                  </div>
                </Form>

              </div>
            </Col>

          </Row>
        </Container>
      </div>
    </>
  );
}

export default LoginCanvas;